import CodeEditor from "../common/code-editor";
import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import { userInfoState, wsReadyState } from "../global-state";
import { ScriptSpecData } from "../lang/script-spec-data";
import { Button, IconButton } from "@mhome/ui";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingSpinner from "../common/loading";
import DeleteAutomationDialog from "./delete-automation-dialog";
import {
  deleteAutomation,
  disableAutomation,
  enableAutomation,
  formatAutomation,
  getAutomation,
  getAutomationDraft,
  saveAutomationScript,
} from "../service/automation-service";
import { useToast } from "../common/toast";
import MSwitch from "../common/switch";
import { useWebSocket } from "../ws/websocket";

const ScriptEditView = () => {
  const specDataRef = useRef(null);
  const history = useHistory();
  const { sendAndWaitWs } = useWebSocket();
  const wsReady = useRecoilValue(wsReadyState);
  const [loading, setLoading] = useState(true);
  const [userInfo] = useRecoilState(userInfoState);
  const [automationId, setAutomationId] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [runnable, setRunnable] = useState(false);
  const [code, setCode] = useState("");
  const [parseResults, setParseResults] = useState([]);
  const [unsaved, setUnsaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const { successToast, warningToast, errorToast } = useToast();
  const location = useLocation();

  useEffect(() => {
    if (!wsReady) {
      return;
    }
    if (!userInfo.setting?.scriptMode) {
      setLoading(true);
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }
    if (params["id"]) {
      handleGetAutomation(params);
    } else if (params["draft"]) {
      handleGetAutomationDraft(params);
    } else {
      setLoading(false);
    }
  }, [wsReady]);

  const handleGetAutomation = async (params) => {
    setLoading(true);
    const resp = await getAutomation(sendAndWaitWs, params.id, true);
    if (resp) {
      setAutomationId(resp["id"]);
      setCode(resp["script"]);
      setParseResults(resp["parseResults"]);
      setRunnable(resp["runnable"]);
      setEnabled(resp["enabled"]);
      setLoading(false);
    } else {
      errorToast("Error");
    }
  };

  const handleGetAutomationDraft = async (params) => {
    setLoading(true);
    const resp = await getAutomationDraft(sendAndWaitWs, params.draft, true);
    if (resp) {
      setAutomationId(null);
      setCode(resp["script"]);
      setParseResults([]);
      setRunnable(false);
      setEnabled(false);
      setLoading(false);
    } else {
      errorToast("Error");
    }
  };

  const handleScriptContext = async (context) => {
    // if (context.integration) {
    //   await loadConnectInfo(context.integration);
    //   setCurrentIntegration(context.integration);
    // } else {
    //   setCurrentIntegration("");
    // }
  };

  const saveScript = async () => {
    try {
      const jsonData = await saveAutomationScript(
        sendAndWaitWs,
        code,
        automationId,
        userInfo.setting?.debugMode
      );
      if (jsonData["runnable"]) {
        successToast("Saved. Ready to activate");
        setRunnable(true);
      } else {
        warningToast("Saved. Script has errors");
        setRunnable(false);
      }
      setUnsaved(false);
      setEnabled(false);
      setParseResults(jsonData["parseResults"]);
      if (!automationId) {
        history.replace(`/automation/script?id=${jsonData["automationId"]}`);
        setAutomationId(jsonData["automationId"]);
      }
    } catch (error) {
      console.error("Error saving draft:", error);
    }
  };

  const toggleEnableDisable = async (event, enableDisable) => {
    if (enableDisable) {
      const response = await enableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Enabled");
        setEnabled(response.enabled);
      } else {
        errorToast("Failed to enable");
      }
    } else {
      const response = await disableAutomation(sendAndWaitWs, automationId);
      if (response && !response.error) {
        successToast("Disabled");
        setEnabled(response.enabled);
      } else {
        errorToast("Failed to disable");
      }
    }
  };

  const handleDelete = async () => {
    const success = await deleteAutomation(sendAndWaitWs, automationId, true);
    if (success) {
      history.push("/automation/list");
    } else {
      errorToast("Fail to delete");
    }
  };

  const formatScript = async () => {
    const resp = await formatAutomation(sendAndWaitWs, code);
    if (resp) {
      if (resp["success"]) {
        setCode(resp["script"]);
        setUnsaved(true);
        successToast("Formatted");
      } else {
        errorToast("Script has errors");
      }
      setParseResults(resp["parseResults"]);
    } else {
      errorToast("Format error");
    }
  };

  const goOverview = () => {
    if (automationId) {
      history.replace(`/automation/overview?id=${automationId}`);
    } else {
      history.replace(`/automation/overview`);
    }
  };

  const handleOAuthFailure = () => {
    errorToast("Error");
  };

  const handleOAuthSuccess = () => {
    successToast("User connected.");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="flex-grow p-4">
          <div className="flex flex-col gap-4 justify-center">
            <div className="w-auto flex-grow-0 flex-shrink">
              <div className="inline-block">
                <div className="flex items-center">
                  <div className="flex flex-row justify-start items-center gap-4">
                    <div className="flex-1">
                      <Button
                        variant="contained"
                        size="medium"
                        onClick={saveScript}
                        disabled={!unsaved}
                      >
                        Save
                      </Button>
                    </div>
                    <div className="flex-1">
                      <Button
                        variant="contained"
                        size="medium"
                        onClick={formatScript}
                        // disabled={!runnable}
                      >
                        Format
                      </Button>
                    </div>
                    <div className="flex-1">
                      {runnable && (
                        <MSwitch
                          checked={enabled}
                          disabled={!runnable || unsaved}
                          onChange={toggleEnableDisable}
                          inputProps={{ "aria-label": "controlled" }}
                        />
                      )}
                    </div>
                    <div>
                      <IconButton
                        color="error"
                        aria-label="delete"
                        onClick={() => setDeleteConfirm(true)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                    <div className="flex-1">
                      <Button
                        variant="contained"
                        size="medium"
                        onClick={goOverview}
                      >
                        overview
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-[11]">
              <div className="flex-grow">
                <div className="flex flex-col gap-4">
                  <div className="w-full">
                    <CodeEditor
                      value={code}
                      editable={true}
                      parseResults={parseResults}
                      // specDataRef={specDataRef}
                      onPreParse={handleScriptContext}
                      height={"800px"}
                      onChange={(instance) => {
                        const code = instance.getValue();
                        setCode(code);
                        setUnsaved(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ScriptSpecData ref={specDataRef} />
          <DeleteAutomationDialog
            open={deleteConfirm}
            onClose={() => setDeleteConfirm(false)}
            onDelete={handleDelete}
          ></DeleteAutomationDialog>
        </div>
      )}
    </div>
  );
};

export default ScriptEditView;
